// File: src/components/admin/SubscriptionUsersView.js
// filtered users list for subscription drill-downs.
// Reads the overview response and slices the requested group based on URL params:
//   ?state=PAID_CANCELLED  → "Churn risk" — users cancelling at period end
//   ?overQuota=1           → "Over quota" — free-plan users at ≥100% of monthlyAiActions
// Other future filters can be added without touching the BE.

import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, Alert, CircularProgress, Button,
    Table, TableBody, TableCell, TableHead, TableRow, Chip,
} from '@mui/material';
import { mainApi } from '../../services/mainApi';

function formatDate(ms) {
    if (!ms) return '—';
    return new Date(ms).toLocaleDateString();
}

export default function SubscriptionUsersView() {
    const [searchParams] = useSearchParams();
    const stateFilter = searchParams.get('state');
    const overQuota = searchParams.get('overQuota') === '1';

    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        mainApi.getSubscriptionOverview()
            .then((data) => { if (!cancelled) setOverview(data); })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
    }
    if (!overview) return null;

    let title, rows, columns;
    if (overQuota) {
        title = 'Users over their monthly AI-action quota';
        rows = overview.usage?.overQuotaUsers || [];
        columns = ['Email', 'Plan', 'Used', 'Cap'];
    } else if (stateFilter === 'PAID_CANCELLED') {
        title = 'Subscribers cancelling at period end';
        rows = overview.churnRiskUsers || [];
        columns = ['Email', 'Cancels on'];
    } else {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">
                    Pick a drill-down from the <RouterLink to="/subscription">Subscription overview</RouterLink>.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }} data-testid="sub-users-view">
            <Button component={RouterLink} to="/subscription" size="small" sx={{ mb: 2 }}>
                ← Back to overview
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {rows.length} user{rows.length === 1 ? '' : 's'}
            </Typography>
            <Card variant="outlined">
                <CardContent>
                    {rows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" data-testid="sub-users-empty">
                            Nobody here right now — good news.
                        </Typography>
                    ) : (
                        <Table size="small" data-testid="sub-users-table">
                            <TableHead>
                                <TableRow>
                                    {columns.map(c => <TableCell key={c}>{c}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow
                                        key={r.uid}
                                        hover
                                        data-testid={`sub-users-row-${i}`}
                                        component={RouterLink}
                                        to={`/users/${r.uid}/subscription`}
                                        sx={{ textDecoration: 'none', cursor: 'pointer' }}
                                    >
                                        <TableCell>{r.email || r.uid}</TableCell>
                                        {overQuota && (
                                            <>
                                                <TableCell><Chip size="small" label={r.planId} /></TableCell>
                                                <TableCell>{r.aiActionsThisMonth?.toLocaleString()}</TableCell>
                                                <TableCell>{r.cap?.toLocaleString()}</TableCell>
                                            </>
                                        )}
                                        {stateFilter === 'PAID_CANCELLED' && (
                                            <TableCell>{formatDate(r.currentPeriodEnd)}</TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
